import React, { useState } from "react";
import {
  ChevronDown,
  Building2,
  Truck,
  FileText,
  Eye,
  X,
  Trash2,
  History,
  Send,
} from "lucide-react";
import { Order } from "./types";
import { getPostOfficesForCountry, needsPostOfficeSelection } from "./utils";

interface PostOfficeSelectorProps {
  order: Order;
  onSelectPostOffice: (orderId: number, postOffice: string) => Promise<void>;
  onClearPostOffice: (orderId: number) => Promise<void>;
  onSendToShipping: (orderId: number, postOffice: string) => Promise<void>;
  onPrintLabel: (orderId: number, labelType: "a6" | "a4") => Promise<void>;
  onViewLabel: (orderId: number, labelType: "a6" | "a4") => void;
  onDeleteShipment?: (orderId: number) => Promise<void>;
  isLoading?: boolean;
}

const PostOfficeSelector: React.FC<PostOfficeSelectorProps> = ({
  order,
  onSelectPostOffice,
  onClearPostOffice,
  onSendToShipping,
  onPrintLabel,
  onViewLabel,
  onDeleteShipment,
  isLoading = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPrinting, setIsPrinting] = useState<"a6" | "a4" | null>(null);

  const postOffices = getPostOfficesForCountry(order.country);
  const needsSelection = needsPostOfficeSelection(order);
  const hasSelectedPostOffice = order.selectedPostOffice && !order.trackingId;
  const isShipped = order.trackingId && order.shippingCompany;

  // Handle post office selection
  const handleSelectPostOffice = async (postOfficeId: string) => {
    setIsSelecting(true);
    try {
      await onSelectPostOffice(order.id, postOfficeId);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Failed to select post office:", error);
    } finally {
      setIsSelecting(false);
    }
  };

  // Handle clearing post office selection
  const handleClearPostOffice = async () => {
    setIsClearing(true);
    try {
      await onClearPostOffice(order.id);
    } catch (error) {
      console.error("Failed to clear post office:", error);
    } finally {
      setIsClearing(false);
    }
  };

  // Handle shipping
  const handleSendToShipping = async () => {
    if (!order.selectedPostOffice) return;

    setIsSending(true);
    try {
      await onSendToShipping(order.id, order.selectedPostOffice);
    } catch (error) {
      console.error("Failed to send to shipping:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Handle label printing
  const handlePrintLabel = async (labelType: "a6" | "a4") => {
    setIsPrinting(labelType);
    try {
      await onPrintLabel(order.id, labelType);
    } catch (error) {
      console.error("Failed to print label:", error);
    } finally {
      setIsPrinting(null);
    }
  };

  // Handle shipment deletion
  const handleDeleteShipment = async () => {
    if (!onDeleteShipment) return;

    if (
      !window.confirm(
        `Are you sure you want to delete shipment ${order.trackingId}?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteShipment(order.id);
    } catch (error) {
      console.error("Failed to delete shipment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // If order needs post office selection
  if (needsSelection) {
    return (
      <div className="relative overflow-visible">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isLoading || isSelecting}
          className="flex items-center space-x-2 bg-yellow-500 text-white px-3 py-1 rounded text-xs hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSelecting ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Building2 className="w-3 h-3" />
          )}
          <span>Select Post Office</span>
          <ChevronDown className="w-3 h-3" />
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-64 max-w-64">
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 mb-2 px-2">
                Select Post Office
              </div>
              <div className="flex flex-col space-y-1">
                {postOffices.map((postOffice) => (
                  <button
                    key={postOffice.id}
                    onClick={() => handleSelectPostOffice(postOffice.id)}
                    disabled={
                      postOffice.status === "disconnected" || isSelecting
                    }
                    className={`block w-full text-left px-2 py-2 rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      postOffice.status === "disconnected"
                        ? "text-gray-400 bg-gray-50"
                        : "text-gray-700 hover:bg-blue-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <Building2 className="w-3 h-3 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-sm truncate">
                          {postOffice.name}
                        </span>
                      </div>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          postOffice.status === "connected"
                            ? "bg-green-100 text-green-700"
                            : postOffice.status === "paused"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {postOffice.status === "connected"
                          ? "✓"
                          : postOffice.status === "paused"
                          ? "⏸"
                          : "✗"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 ml-5 truncate">
                      {postOffice.description || `${postOffice.name} service`}
                    </div>
                    {postOffice.status === "connected" && (
                      <div className="text-xs text-green-600 ml-5 mt-0.5 font-medium">
                        Ready to ship
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="border-t border-gray-100 mt-2 pt-2">
                <div className="text-xs text-gray-400 px-2">
                  Select a post office to proceed
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // If post office is selected but not shipped yet
  if (hasSelectedPostOffice) {
    const selectedPostOffice = postOffices.find(
      (po) => po.id === order.selectedPostOffice
    );

    return (
      <div className="flex items-center space-x-2">
        <div
          className="relative flex items-center space-x-1 px-2 py-1 pr-6 rounded-md border"
          style={{
            backgroundColor: "#f0f4f2",
            borderColor: "#a8b5a8",
          }}
        >
          <Building2 className="w-3 h-3" style={{ color: "#264a3e" }} />
          <span className="text-xs font-medium" style={{ color: "#264a3e" }}>
            {selectedPostOffice?.name || order.selectedPostOffice}
          </span>
          <span className="text-xs" style={{ color: "#5a6b5a" }}>
            • Ready
          </span>

          {/* X button in top-right corner */}
          <button
            onClick={handleClearPostOffice}
            disabled={isLoading || isClearing}
            className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-gray-400 hover:bg-gray-500 text-white rounded-full text-xs disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            title="Clear post office selection"
          >
            {isClearing ? (
              <div className="w-2 h-2 border border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <X className="w-2.5 h-2.5" />
            )}
          </button>
        </div>
        <button
          onClick={handleSendToShipping}
          disabled={isLoading || isSending}
          className="flex items-center space-x-1 text-white px-3 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ backgroundColor: "#264a3e" }}
        >
          {isSending ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Truck className="w-3 h-3" />
          )}
          <span>{isSending ? "Sending..." : "Ship"}</span>
        </button>
      </div>
    );
  }

  // If order is shipped, show label actions
  if (isShipped) {
    const shippedPostOffice = postOffices.find(
      (po) => po.id === order.shippingCompany
    );

    // For ACS, show single print button
    if (order.shippingCompany === "acs") {
      return (
        <div className="flex items-center space-x-2">
          {/* <div className="flex items-center space-x-1 bg-green-50 px-2 py-1 rounded-md border border-green-200">
            <Building2 className="w-3 h-3 text-green-600" />
            <span className="text-xs text-green-700 font-medium">
              {shippedPostOffice?.name || "ACS"}
            </span>
            <span className="text-xs text-green-500">• Shipped</span>
          </div> */}
          {/* Preview ACS Voucher */}
          <button
            onClick={() => handlePrintLabel("a6")} // Use a6 as default for ACS
            disabled={isLoading || isPrinting === "a6"}
            className="flex items-center space-x-1 text-white px-2 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: "#264a3e" }}
            title="Preview ACS Voucher"
          >
            {isPrinting === "a6" ? (
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <FileText className="w-3 h-3" />
            )}
            <span>Preview</span>
          </button>

          {/* View Tracking */}
          <button
            onClick={() => onViewLabel(order.id, "a6")}
            className="flex items-center space-x-1 bg-gray-600 text-white px-2 py-1 rounded text-xs hover:bg-gray-700 transition-colors"
            title="View Tracking History"
          >
            <History className="w-3 h-3" />
          </button>

          {/* Delete Voucher */}
          <button
            onClick={handleDeleteShipment}
            disabled={isLoading || isDeleting}
            className="flex items-center space-x-1 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Delete ACS Voucher"
          >
            {isDeleting ? (
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Trash2 className="w-3 h-3" />
            )}
          </button>
        </div>
      );
    }

    // For EasyMail and other companies, show A6 and A4 buttons
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1 bg-green-50 px-2 py-1 rounded-md border border-green-200">
          <Building2 className="w-3 h-3 text-green-600" />
          <span className="text-xs text-green-700 font-medium">
            {shippedPostOffice?.name || order.shippingCompany}
          </span>
          <span className="text-xs text-green-500">• Shipped</span>
        </div>
        {/* Preview A6 Label */}
        <button
          onClick={() => handlePrintLabel("a6")}
          disabled={isLoading || isPrinting === "a6"}
          className="flex items-center space-x-1 text-white px-2 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ backgroundColor: "#264a3e" }}
          title="Preview A6 Label"
        >
          {isPrinting === "a6" ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Eye className="w-3 h-3" />
          )}
          <span>A6</span>
        </button>

        {/* Preview A4 Envelope */}
        <button
          onClick={() => handlePrintLabel("a4")}
          disabled={isLoading || isPrinting === "a4"}
          className="flex items-center space-x-1 text-white px-2 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ backgroundColor: "#264a3e" }}
          title="Preview A4 Envelope"
        >
          {isPrinting === "a4" ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Eye className="w-3 h-3" />
          )}
          <span>A4</span>
        </button>

        {/* View Tracking */}
        <button
          onClick={() => onViewLabel(order.id, "a6")}
          className="flex items-center space-x-1 bg-gray-600 text-white px-2 py-1 rounded text-xs hover:bg-gray-700 transition-colors"
          title="View Tracking History"
        >
          <History className="w-3 h-3" />
        </button>

        {/* Delete Shipment */}
        <button
          onClick={handleDeleteShipment}
          disabled={isLoading || isDeleting}
          className="flex items-center space-x-1 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Delete Shipment"
        >
          {isDeleting ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Trash2 className="w-3 h-3" />
          )}
        </button>
      </div>
    );
  }

  // Default state - for Greece orders without post office (auto-ACS)
  // Show "Send to Shipping" button directly
  const countryLower = order.country?.toLowerCase();
  if (countryLower === 'gr' || countryLower === 'greece') {
    return (
      <button
        onClick={() => onSendToShipping(order.id, 'acs')}
        disabled={isLoading || isSending}
        className="flex items-center space-x-1 text-white px-3 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 transition-colors"
        style={{ backgroundColor: "#264a3e" }}
        title="Send to ACS"
      >
        {isSending ? (
          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <Send className="w-3 h-3" />
        )}
        <span>Send to ACS</span>
      </button>
    );
  }

  return <span className="text-xs text-gray-500">Ready</span>;
};

export default PostOfficeSelector;
